import { useReducer } from 'react'
import {
    CopyStates,
    JourneyAction,
    JourneyChangesPack,
    JourneyDispatchActionPack,
    JourneyNodeAddPack,
    JourneyNodeEnrichPack,
    JourneyNodeRemovePack,
    JourneyNodeStepChangePack,
    JourneyProps,
    JourneyReducerElements,
    JourneySetPack,
    JourneyState,
} from './types/journey-context'
import { JourneyNode, JourneyStepData } from './types/journey-nodes'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { isBranchingNode } from './utils/graph/traversal'
import { replacePlaceholderWithStep, getRemoveNodeMutations, getBranchingNodeUpdateMutations } from './helpers/graph-mutations'
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react'
import { JourneyPlaceholderReplacePack } from './types/journey-context'
import { simpleNotification } from '../../_utils/utils'

export const useJourneyReducer = (props: JourneyProps): JourneyReducerElements => {
    const [context, dispatch] = useReducer(handleJourneyActionRequest, getJourneyStateInitializer(props))
    return [context, dispatch]
}

export function getJourneyStateInitializer(props: JourneyProps): JourneyState {
    return {
        ...props,
        nodes: props.nodes ?? [],
        activeNode: props.activeNode,
        edges: props.edges ?? [],
        removedNodes: [],
        dependencies: {},
        copy: props.copy ?? { state: 'clear', node: null },
        closePopoversSignal: 0,
    }
}

export function handleJourneyActionRequest(state: JourneyState, pack: JourneyDispatchActionPack): JourneyState {
    let currentState = { ...state }

    switch (pack.action) {
        case JourneyAction.SET_JOURNEY:
            return handleJourneySetRequest(currentState, pack)
        case JourneyAction.SET_ACTIVE_NODE: {
            currentState.activeNode = pack.data ?? undefined
            currentState.editorOpen = !!pack.data
            if (!pack.data) {
                currentState.editorFooter = undefined
            }

            try {
                const currPath = new URL(location.href)
                const qs = currPath.searchParams
                let shouldUpdatePath = false

                if (pack.data) {
                    if (!qs.has('step_id') || qs.get('step_id') !== pack.data.id) {
                        qs.set('step_id', pack.data.id ?? '')
                        shouldUpdatePath = true
                    }
                } else {
                    if (qs.has('step_id')) {
                        qs.delete('step_id')
                        shouldUpdatePath = true
                    }
                }

                if (shouldUpdatePath) {
                    // replaceState is needed to update the URL without triggering a new history entry
                    history.replaceState(null, '', currPath.toString())
                }
            } catch {}

            return currentState
        }
        case JourneyAction.ON_NODE_ADD:
            return handleJourneyNodeAddRequest(currentState, pack)
        case JourneyAction.ON_NODE_STEP_CHANGE:
            return handleNodeStepChangeRequest(currentState, pack)
        case JourneyAction.ON_NODE_ENRICH:
            return handleNodeEnrichRequest(currentState, pack)
        case JourneyAction.ON_NODE_REMOVE:
            return handleJourneyNodeRemoveRequest(currentState, pack)
        case JourneyAction.ON_PLACEHOLDER_REPLACE:
            return handlePlaceholderReplaceRequest(currentState, pack as JourneyPlaceholderReplacePack)
        case JourneyAction.ON_JOURNEY_CHANGES:
            return handleJourneyChangeRequest(currentState, pack)
        case JourneyAction.SET_JOURNEY_DEPENDENCIES:
            currentState.dependencies = pack.data
            return currentState
        case JourneyAction.SET_JOURNEY_INTEGRATIONS:
            currentState.dependencies.integrations = pack.data
            return currentState
        case JourneyAction.SET_COPY_STATE:
            currentState.copy =
                pack.data.node === null
                    ? { state: CopyStates.CLEAR, node: pack.data.node }
                    : { state: CopyStates.FILLED, type: pack.data.type, node: pack.data.node }
            return currentState
        case JourneyAction.SET_EDITOR_OPEN: {
            currentState.editorOpen = pack.data

            if (!pack.data) {
                currentState.editorFooter = undefined
            }

            return currentState
        }
        case JourneyAction.SET_EDITOR_FOOTER: {
            currentState.editorFooter = pack.data ?? undefined
            return currentState
        }
        case JourneyAction.CLOSE_ALL_POPOVERS: {
            currentState.closePopoversSignal = (currentState.closePopoversSignal ?? 0) + 1
            return currentState
        }
        default:
            return currentState
    }
}

function handleJourneySetRequest(state: JourneyState, pack: JourneySetPack): JourneyState {
    return {
        ...state,
        journey: pack.data,
    }
}

function handleJourneyNodeAddRequest(state: JourneyState, pack: JourneyNodeAddPack) {
    const node = pack.data
    if (node.data.step.type !== StepType.EXIT && node.data.step.type !== StepType.TRIGGER) {
        state.activeNode = node
        state.editorOpen = true
    }
    return state
}


/**
 * Creates a new JourneyNode with the given step, preserving all React Flow
 * metadata (position, measured, type, etc.) and carrying forward existing uiMeta.
 * The original node is never modified.
 */
export function cloneNodeWithStep(
    node: JourneyNode,
    newStep: IJourneyStep,
): JourneyNode {
    const newData = new JourneyStepData({
        step: newStep,
        uiMeta: { ...node.data.uiMeta },
    })
    return { ...node, data: newData }
}

function handleNodeStepChangeRequest(
    state: JourneyState,
    pack: JourneyNodeStepChangePack,
): JourneyState {
    const { nodeId, step: newStep, clearInvalid } = pack.data
    const currentState = { ...state }

    // 1. Find the existing node (old state, pre-mutation)
    const oldNode = currentState.nodes.find((n) => n.id === nodeId)
    if (!oldNode) return currentState

    // 2. Build the replacement node without mutating the old one
    let newNode = cloneNodeWithStep(oldNode, newStep)

    // 2a. If editor signals invalid state should be cleared, update uiMeta
    if (clearInvalid) {
        newNode = {
            ...newNode,
            data: new JourneyStepData({
                step: newNode.data.step,
                uiMeta: { ...newNode.data.uiMeta, invalid: undefined },
            }),
        }
    }

    // 3. Replace the node in the nodes array
    currentState.nodes = currentState.nodes.map((n) =>
        n.id === nodeId ? newNode : n,
    )

    // 4. If branching node, compute edge changes from clean old/new diff
    if (isBranchingNode(oldNode)) {
        const { edgeChanges, orphanedNodeIds } = getBranchingNodeUpdateMutations(
            oldNode,
            newStep,
            currentState.edges,
            currentState.nodes,
        )

        // Apply edge changes
        currentState.edges = applyEdgeChanges(edgeChanges, currentState.edges)

        // Remove orphaned nodes and their edges
        if (orphanedNodeIds.size > 0) {
            currentState.nodes = currentState.nodes.filter((n) => !orphanedNodeIds.has(n.id))
            currentState.edges = currentState.edges.filter(
                (e) => !orphanedNodeIds.has(e.source) && !orphanedNodeIds.has(e.target),
            )
        }
    }

    return currentState
}

function handleNodeEnrichRequest(
    state: JourneyState,
    pack: JourneyNodeEnrichPack,
): JourneyState {
    const { nodeId, enrichedStep } = pack.data
    const currentState = { ...state }

    const oldNode = currentState.nodes.find((n) => n.id === nodeId)
    if (!oldNode) return currentState

    const newNode = cloneNodeWithStep(oldNode, enrichedStep)
    currentState.nodes = currentState.nodes.map((n) =>
        n.id === nodeId ? newNode : n,
    )

    return currentState
}

function handleJourneyNodeRemoveRequest(state: JourneyState, pack: JourneyNodeRemovePack) {
    const node = pack.data
    if (node.data.step.type !== StepType.EXIT && node.data.step.type !== StepType.TRIGGER) {
        // Defensive guard: if this node is a placeholder, block deletion and surface warning.
        // Primary callers (interaction actions) already check getRemoveNodeMutations before
        // dispatching ON_NODE_REMOVE, but this ensures no code path bypasses the guard.
        const [, , error] = getRemoveNodeMutations(node, state.nodes, state.edges)
        if (error) {
            simpleNotification('warning', error.message)
            return state
        }

        state.removedNodes = [...state.removedNodes, node]
    }
    return state
}

function handleJourneyChangeRequest(state: JourneyState, pack: JourneyChangesPack) {
    if (pack.data.nodes) {
        state.nodes = pack.data.nodes
    }

    if (pack.data.edges) {
        state.edges = pack.data.edges
    }

    return state
}

/**
 * Handles replacing an empty placeholder node with a real step type.
 *
 * This occurs when a brand-new journey is created — the initial graph contains a
 * PLACEHOLDER node between the TRIGGER and EXIT steps. When the user selects a step
 * type from the placeholder's inline picker, this handler swaps the placeholder for
 * a fully-configured step of the chosen type, rewires edges, and opens the editor
 * drawer for the new step.
 */
function handlePlaceholderReplaceRequest(state: JourneyState, pack: JourneyPlaceholderReplacePack): JourneyState {
    const { node, stepType } = pack.data

    // Find the placeholder node in state by ID
    const placeholderNode = state.nodes.find((n) => n.id === node.id)
    if (!placeholderNode) return state

    const [nodeChanges, edgeChanges] = replacePlaceholderWithStep(
        placeholderNode,
        stepType,
        state.nodes,
        state.edges,
    )

    state.nodes = applyNodeChanges(nodeChanges, state.nodes)
    state.edges = applyEdgeChanges(edgeChanges, state.edges)

    // Set the replaced node as active and open editor
    const replacedNode = state.nodes.find((n) => n.id === node.id)
    if (replacedNode) {
        state.activeNode = replacedNode
        state.editorOpen = true
    }

    return state
}
